'use client'

import { useTranslations as useI18nTranslations } from 'next-intl'
import { useI18nAvailable } from '@/contexts/I18nContext'

// Type for the translation function — supports ICU-style {var} interpolation via params
type TranslateFunction = (key: string, params?: Record<string, string | number | Date | boolean | null | undefined>) => string

// Fallback translations for each namespace
const fallbacks: Record<string, Record<string, string>> = {
  common: {
    contactUs: 'Contact Us',
    bookNow: 'Book Now',
    viewAllLodges: 'VIEW ALL LODGE DESTINATIONS',
    viewAllAdventures: 'VIEW ALL GUIDED ADVENTURES',
    back: 'Back',
    menu: 'MENU',
    home: 'Home',
    lodges: 'Lodge Destinations',
    adventures: 'Guided Adventures',
    events: 'Events',
    testimonials: 'Testimonials',
    whyApex: 'Why Apex?',
    contact: 'Contact'
  },
  header: {
    lodgeDestinations: 'Lodge Destinations',
    guidedAdventures: 'Guided Adventures',
    ariaMainMenu: 'Main Menu',
    ariaBackToMenu: 'Back to main menu'
  },
  footer: {
    lodges: 'Lodges',
    adventures: 'Adventures',
    about: 'About',
    whyApex: 'Why Apex',
    contact: 'Contact',
    contactTitle: 'Contact us',
    getInTouch: 'Get in Touch',
    copyright: '2025 Apex Experience, LLC. All Rights Reserved. 330 Belleview Ave, Crested Butte, CO 81224',
    // Lodge names
    apexMountainPenthouse: 'Apex Mountain Penthouse',
    apexParvaCave: 'Apex Parva Cave',
    apexPremiumLodge: 'Apex Premium Lodge',
    apexYelchoPatagonia: 'Apex Yelcho Patagonia',
    apexPatagoniaLodge: 'Apex Patagonia Lodge',
    apexLakefrontSerenity: 'Apex Lakefront Serenity',
    // Adventure names
    resortSkiing: 'Resort Skiing',
    heliSkiing: 'Heli-skiing',
    skiTouring: 'Ski Touring',
    catSkiing: 'Cat Skiing',
    flyFishing: 'Fly Fishing',
    waterSkiing: 'Water Skiing',
    // Social
    instagram: 'Instagram'
  },
  'lodges.regions': {
    laParvaChile: 'La Parva, Chile',
    farellonesChile: 'Farellones, Chile',
    patagoniaChile: 'Patagonia, Chile',
    araucaniaChile: 'Araucania, Chile'
  },
  intro: {
    meta: 'Apex',
    established: 'Est. 2022',
    text: "At Apex, your safety comes first. Every detail is handled so you can focus on adventure. Ride helicopters, snowmobiles, and ski the Southern Hemisphere's best terrain—where endless runs meet pure freedom. No flight limits mean your journey never stops. If mountains close, we fly to the coast, vineyards, or city. In summer, we head to Patagonia for world-class fly fishing and untouched lakes. With Apex, there are no down days—only stories no one will believe"
  },
  languageSwitcher: {
    selectLanguage: 'Select Language'
  },
  booking: {
    'hero.overline': 'Book Your Adventure',
    'hero.title': 'Create Your Perfect Experience',
    'hero.subtitle': "Choose how you want to explore. Whether it's a luxury lodge stay, a guided adventure, or a complete package, we'll craft an unforgettable journey tailored to you.",
    'options.lodges.title': 'Book a Lodge',
    'options.lodges.description': 'Choose from our collection of exclusive mountain lodges and lakeside retreats. Each property offers a unique blend of luxury, adventure, and authentic Chilean hospitality.',
    'options.lodges.countLabel': 'Properties',
    'options.activities.title': 'Book an Activity',
    'options.activities.description': 'Embark on world-class guided adventures. From heli-skiing in pristine powder to fly fishing in crystal-clear rivers, our expert guides ensure unforgettable experiences.',
    'options.activities.countLabel': 'Adventures',
    'options.packages.title': 'Book a Package',
    'options.packages.description': 'Experience the best of Apex with our curated packages. Combine accommodation, activities, and exclusive access for the ultimate adventure vacation.',
    'options.packages.countLabel': 'Packages',
    'options.exploreOptions': 'Explore Options',
    'features.title': 'Why Book With Apex',
    'features.secureBooking.title': 'Secure Booking',
    'features.secureBooking.description': 'Your payment details are protected with industry-leading encryption and secure payment processing.',
    'features.instantConfirmation.title': 'Instant Confirmation',
    'features.instantConfirmation.description': 'Receive immediate booking confirmation with all details sent directly to your email.',
    'features.personalizedService.title': 'Personalized Service',
    'features.personalizedService.description': 'Our dedicated concierge team is available to customize your experience and handle every detail.',
    'features.flexibleCancellation.title': 'Flexible Cancellation',
    'features.flexibleCancellation.description': 'Plans change. Our flexible cancellation policies give you peace of mind when booking your adventure.',
    'cta.title': 'Need Help Planning?',
    'cta.description': 'Our adventure specialists are ready to help you create the perfect experience. Contact us for personalized recommendations and custom itineraries.',
    'cta.contactUs': 'Contact Us',
    'cta.exploreAdventures': 'Explore Adventures'
  },
  checkout: {
    'breadcrumb.booking': 'Booking',
    'breadcrumb.checkout': 'Checkout',
    title: 'Checkout',
    subtitle: 'Review your selections and complete your booking'
  },
  confirmation: {
    title: 'Booking Confirmed',
    subtitle: 'Thank you for your reservation',
    bookingId: 'Booking ID',
    nextSteps: 'Next Steps',
    contactUs: 'Contact Us',
    newBooking: 'New Booking'
  },
  paymentSelector: {
    selectPaymentMethod: 'Select Payment Method',
    comingSoon: 'Coming Soon',
    proceedToPayment: 'Proceed to Payment',
    back: 'Back',
    processingPayment: 'Processing payment...',
    paymentError: 'Payment failed. Please try again.',
    total: 'Total',
    totalToPay: 'Total to pay',
    payApex: 'Pay Apex Experience',
    guestSingular: 'guest',
    guestPlural: 'guests',
    cleaningFee: 'Cleaning fee',
    serviceFee: 'Service fee',
    discount: 'Discount',
    totalDue: 'Total due',
    pixTotalBRL: 'Total in BRL',
    pixExchangeRate: 'Exchange rate',
    lockedBadge: 'Locked',
    adminUnlockPrompt: 'This method is temporarily disabled. Enter the admin password to test it:',
    adminUnlockWrong: 'Incorrect password.'
  },
  stripeCheckout: {
    payNow: 'Pay Now',
    processing: 'Processing...',
    securePayment: 'Secure payment powered by Stripe',
    errorGeneric: 'An error occurred. Please try again.',
    errorCardDeclined: 'Your card was declined. Please try a different card.',
    errorExpired: 'Your card has expired. Please use a different card.',
    errorIncorrectCvc: 'The CVC code is incorrect.',
    errorInsufficientFunds: 'Insufficient funds. Please try a different card.',
    errorProcessing: 'An error occurred while processing. Please try again.',
    errorNetworkFailed: 'Network error. Please check your connection.',
    testMode: 'Test Mode',
    testModeDesc: 'Use card number 4242 4242 4242 4242 for testing',
    orPayWith: 'Or Pay With',
    expressNotAvailable: 'Apple Pay and Google Pay not available on this device'
  },
  mercadoPagoCheckout: {
    payWithMercadoPago: 'Pay with MercadoPago',
    redirecting: 'Redirecting to MercadoPago...',
    securePayment: 'You will be redirected to MercadoPago to complete your payment securely.',
    errorGeneric: 'An error occurred. Please try again.',
    errorBookingNotFound: 'Booking not found. Please try again.',
    errorBookingExpired: 'Your booking has expired. Please create a new booking.',
    errorPreference: 'Could not create payment. Please try again.',
    testMode: 'Test Mode',
    testModeDesc: 'Use test cards from MercadoPago documentation',
    total: 'Total',
    securityNote: 'Secure payment powered by MercadoPago',
    paymentMethods: 'Credit cards, debit cards, and local payment methods accepted',
    bookingTotal: 'Booking total',
    exchangeRate: 'Exchange rate',
    youWillBeCharged: 'You will be charged'
  },
  sumUpCheckout: {
    payWithSumUp: 'Pay with SumUp',
    redirecting: 'Redirecting to SumUp...',
    securePayment: 'You will be redirected to SumUp Hosted Checkout to complete your payment securely.',
    errorGeneric: 'An error occurred. Please try again.',
    errorBookingNotFound: 'Booking not found. Please try again.',
    errorBookingExpired: 'Your booking has expired. Please create a new booking.',
    errorPreference: 'Could not create payment. Please try again.',
    testMode: 'Sandbox Mode',
    testModeDesc: 'Use SumUp sandbox test cards',
    total: 'Total',
    securityNote: 'Secure payment powered by SumUp',
    paymentMethods: 'International and local cards accepted via SumUp Compraqui (Chile)',
    bookingTotal: 'Booking total',
    exchangeRate: 'Exchange rate',
    youWillBeCharged: 'You will be charged',
    resumeBanner: 'You have a pending payment with SumUp',
    resumeAction: 'Resume',
    cancelAction: 'Cancel'
  },
  dualPrice: {
    loading: 'Loading...',
    willBeCharged: 'will be charged',
    exchangeRate: 'Exchange rate',
    conversion: 'Currency Conversion',
    originalAmount: 'Original amount',
    chargedAmount: 'You will be charged',
    rateSource: 'Rate source',
    lastUpdated: 'Last updated',
    staleWarning: 'Rate may be outdated'
  },
  imageGallery: {
    lightbox: 'Image lightbox',
    close: 'Close lightbox',
    prev: 'Previous image',
    next: 'Next image'
  }
}

/**
 * Create a fallback translation function for a given namespace
 */
function createFallback(namespace: string): TranslateFunction {
  const namespaceFallback = fallbacks[namespace] || {}
  return (key, params) => {
    let value = namespaceFallback[key] || key
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), v == null ? '' : String(v))
      }
    }
    return value
  }
}

/**
 * Hook that uses i18n translations when available.
 * Must ONLY be used inside i18n context (within [locale] routes).
 */
export function useTranslations(namespace: string): TranslateFunction {
  return useI18nTranslations(namespace) as unknown as TranslateFunction
}

/**
 * Safe translation hook that works both inside and outside i18n context.
 *
 * This hook checks the context and returns appropriate translations:
 * - In i18n context: returns actual translations from next-intl
 * - Outside i18n context: returns fallback strings
 *
 * IMPORTANT: This uses the I18nContextProvider to safely determine if we're
 * inside an i18n context without calling hooks conditionally.
 */
export function useSafeTranslations(namespace: string): TranslateFunction {
  const isI18nAvailable = useI18nAvailable()

  // We cannot call useI18nTranslations conditionally, so we return early
  // when not in i18n context and avoid the hook entirely
  if (!isI18nAvailable) {
    return createFallback(namespace)
  }

  // In i18n context, we can safely use translations
  // This is safe because isI18nAvailable is stable within a render
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useI18nTranslations(namespace) as unknown as TranslateFunction
}
